import { SickLeavePage } from "@/pages/SickLeavePage";
import { OzoRequestsPage } from "@/pages/OzoRequestsPage";
import { HelpPage } from "@/pages/HelpPage";
import { NewsPage } from "@/pages/NewsPage";
import { SupportPage, SupportDetailPage } from "@/pages/SupportPage";
import { FamilyProcessPage } from "@/pages/FamilyProcessPage";
import { EquipmentIssuePage } from "@/pages/EquipmentIssuePage";
import { EquipmentInventoryPage } from "@/pages/EquipmentInventoryPage";
import { SiteEquipmentPage } from "@/pages/SiteEquipmentPage";
import { EquipmentQrPage } from "@/pages/EquipmentQrPage";
import { SuppliersPage, SupplierPage } from "@/pages/SuppliersPage";
import { ProcurementPage } from "@/pages/ProcurementPage";
import { ReceivedInvoicesPage } from "@/pages/ReceivedInvoicesPage";
import { ReceivedInvoicePage } from "@/pages/ReceivedInvoicePage";
import { ReceivedInvoiceManagementPage } from "@/pages/ReceivedInvoiceManagementPage";
import { ExpenseWithoutOrderPage } from "@/pages/ExpenseWithoutOrderPage";
import { ProcurementNewPage } from "@/pages/ProcurementNewPage";
import { ConstructionOverviewPage } from "@/components/nabava/ConstructionOverview";
import { ProcurementCasePage } from "@/pages/ProcurementCasePage";
import { ProcurementApprovalsPage } from "@/pages/ProcurementApprovalsPage";
import { HealthSafetyCasePage } from "@/pages/HealthSafetyCasePage";
import { PermitDeregistrationPage } from "@/pages/PermitDeregistrationPage";
import { PermitCasePage } from "@/pages/PermitCasePage";
import { PermitAddressChangePage } from "@/pages/PermitAddressChangePage";
import { PermitTemplatesPage } from "@/pages/PermitTemplatesPage";
import { PermitDashboardPage } from "@/pages/PermitDashboardPage";
import { RegistrationAddressesPage } from "@/pages/RegistrationAddressesPage";
import { Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";

import { RequireAuth } from "@/components/RequireAuth";
import { PostavkeProvider } from "@/lib/postavke";
import { SesijaCuvar } from "@/components/SesijaCuvar";
import { AuthProvider } from "@/context/AuthContext";
import { RedProvider } from "@/context/RedContext";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { AppShell } from "@/components/layout/AppShell";
import { FormaRadnikaPage } from "@/pages/FormaRadnikaPage";
import { GradilistaPage } from "@/pages/GradilistaPage";
import { GradilisniPregledPage } from "@/pages/GradilisniPregledPage";
import { GradilisniSpisakPage } from "@/pages/GradilisniSpisakPage";
import { KarticaRadnikaPage } from "@/pages/KarticaRadnikaPage";
import { PostavkePage } from "@/pages/PostavkePage";
import { KorisniciPage } from "@/pages/KorisniciPage";
import { UnosSatiPage } from "@/pages/UnosSatiPage";
import { SporedneAktivnostiPage } from "@/pages/SporedneAktivnostiPage";
import { UvozRadnikaPage } from "@/pages/UvozRadnikaPage";
import { PocetnaPage } from "@/pages/PocetnaPage";
import { ObvezeTvrtkePage } from "@/pages/ObvezeTvrtkePage";
import { PopisZaposlenikaPage } from "@/pages/PopisZaposlenikaPage";
import { RokoviObvezePage } from "@/pages/RokoviObvezePage";
import { LoginPage } from "@/pages/LoginPage";
import { TwoFactorPage } from "@/pages/TwoFactorPage";
import { DocumentInboxUploadPage } from "@/pages/DocumentInboxUploadPage";
import { AgencyImportPage } from "@/pages/AgencyImportPage";
import { DocumentInboxReviewPage } from "@/pages/DocumentInboxReviewPage";
import { ScannersPage } from "@/pages/ScannersPage";
import { PermitControlPage } from "@/pages/PermitControlPage";
import type { WorkflowScreen } from "@/pages/WorkflowPage";
const WorkflowPage = lazy(() =>
  import("@/pages/WorkflowPage").then((module) => ({
    default: module.WorkflowPage,
  })),
);

export default function App() {
  return (
    <AuthProvider>
      {/* Inside the auth provider and outside the routes: the queue outlives
          every screen, and signing out must not empty it (ADR-0004 §2.7). */}
      <RedProvider>
        {/* Mounted once, above the routes: one idle clock for the session,
            not one per screen. Renders nothing until it has something to say. */}
        <SesijaCuvar />
        <PostavkeProvider><Routes>
          <Route path="/ozo-zahtjevi" element={<RequireAuth><AppShell><OzoRequestsPage /></AppShell></RequireAuth>} />
          <Route path="/bolovanja" element={<RequireAuth><AppShell><SickLeavePage /></AppShell></RequireAuth>} />
          <Route
            path="/pomoc/novosti"
            element={<RequireAuth><AppShell><NewsPage /></AppShell></RequireAuth>}
          />
          <Route
            path="/pomoc/upute"
            element={
              <RequireAuth>
                <AppShell>
                  <HelpPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route path="/pomoc" element={<RequireAuth><AppShell><SupportPage /></AppShell></RequireAuth>} />
          <Route path="/pomoc/prijave" element={<RequireAuth><AppShell><SupportPage admin /></AppShell></RequireAuth>} />
          <Route path="/pomoc/prijave/:id" element={<RequireAuth><AppShell><SupportDetailPage /></AppShell></RequireAuth>} />
          {(
            [
              "brigade",
              "ucinak-sati",
              "taktovi",
              "operativni-plan",
              "dani-rasporeda",
              "odobravanje-sati",
              "razdoblja",
              "zahtjevi-izmjene",
            ] as WorkflowScreen[]
          ).map((screen) => (
            <Route
              key={screen}
              path={`/${screen}`}
              element={
                <RequireAuth>
                  <AppShell>
                    <Suspense
                      fallback={<p role="status">Učitavanje ekrana…</p>}
                    >
                      <WorkflowPage screen={screen} />
                    </Suspense>
                  </AppShell>
                </RequireAuth>
              }
            />
          ))}
          <Route path="/zdravlje/postupci/:processId" element={<RequireAuth><AppShell><HealthSafetyCasePage kind="medical" /></AppShell></RequireAuth>} />
          <Route path="/znr/postupci/:processId" element={<RequireAuth><AppShell><HealthSafetyCasePage kind="training" /></AppShell></RequireAuth>} />
          <Route path="/dozvole/postupci" element={<RequireAuth><AppShell><PermitDashboardPage /></AppShell></RequireAuth>} />
          <Route path="/adrese-za-prijavu" element={<RequireAuth><AppShell><RegistrationAddressesPage /></AppShell></RequireAuth>} />
          <Route path="/adrese-za-prijavu/:addressId" element={<RequireAuth><AppShell><RegistrationAddressesPage /></AppShell></RequireAuth>} />
          <Route path="/dozvole/predlosci" element={<RequireAuth><AppShell><PermitTemplatesPage /></AppShell></RequireAuth>} />
          <Route path="/dozvole/kontrola" element={<RequireAuth><AppShell><PermitControlPage /></AppShell></RequireAuth>} />
          <Route path="/obitelj/postupci/:processId" element={<RequireAuth><AppShell><FamilyProcessPage /></AppShell></RequireAuth>} />
          <Route path="/dozvole/odjave/:processId" element={<RequireAuth><AppShell><PermitDeregistrationPage /></AppShell></RequireAuth>} />
          <Route path="/dozvole/postupci/:processId" element={<RequireAuth><AppShell><PermitCasePage /></AppShell></RequireAuth>} />
          <Route path="/dozvole/promjene-adrese/:processId" element={<RequireAuth><AppShell><PermitAddressChangePage /></AppShell></RequireAuth>} />
          <Route path="/sporedni-poslovi" element={<RequireAuth><AppShell><SporedneAktivnostiPage /></AppShell></RequireAuth>} />
          <Route path="/oprema/izdaj" element={<RequireAuth><AppShell><EquipmentIssuePage /></AppShell></RequireAuth>} />
          <Route path="/oprema" element={<RequireAuth><AppShell><SiteEquipmentPage /></AppShell></RequireAuth>} />
          <Route path="/oprema/ukupno" element={<RequireAuth><AppShell><SiteEquipmentPage screen="overview" /></AppShell></RequireAuth>} />
          <Route path="/oprema/preseli" element={<RequireAuth><AppShell><SiteEquipmentPage screen="source" /></AppShell></RequireAuth>} />
          <Route path="/oprema/skeniraj" element={<RequireAuth><AppShell><SiteEquipmentPage screen="scan" /></AppShell></RequireAuth>} />
          <Route path="/oprema/naljepnice" element={<RequireAuth><AppShell><SiteEquipmentPage screen="labels" /></AppShell></RequireAuth>} />
          <Route path="/oprema/q/:token" element={<RequireAuth><AppShell><EquipmentQrPage /></AppShell></RequireAuth>} />
          <Route path="/oprema/artikli" element={<RequireAuth><AppShell><SiteEquipmentPage screen="articles" /></AppShell></RequireAuth>} />
          <Route path="/oprema/mjesta" element={<RequireAuth><AppShell><SiteEquipmentPage screen="places" /></AppShell></RequireAuth>} />
          <Route path="/oprema/mjesta/:id" element={<RequireAuth><AppShell><SiteEquipmentPage screen="place" /></AppShell></RequireAuth>} />
          <Route path="/oprema/komadi/:id" element={<RequireAuth><AppShell><SiteEquipmentPage screen="piece" /></AppShell></RequireAuth>} />
          <Route path="/oprema/uvoz" element={<RequireAuth><AppShell><SiteEquipmentPage screen="import" /></AppShell></RequireAuth>} />
          <Route path="/oprema/odobrenja" element={<RequireAuth><AppShell><SiteEquipmentPage screen="approvals" /></AppShell></RequireAuth>} />
          <Route path="/gradilista/:id/oprema/pocetno-stanje" element={<RequireAuth><AppShell><EquipmentInventoryPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/dobavljaci" element={<RequireAuth><AppShell><SuppliersPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/dobavljaci/:id" element={<RequireAuth><AppShell><SupplierPage /></AppShell></RequireAuth>} />
          <Route path="/nabava" element={<RequireAuth><AppShell><ProcurementPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/ulazni-racuni" element={<RequireAuth><AppShell><ReceivedInvoicesPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/ulazni-racuni/:id" element={<RequireAuth><AppShell><ReceivedInvoicePage /></AppShell></RequireAuth>} />
          <Route path="/nabava/ulazni-racuni/uprava" element={<RequireAuth><AppShell><ReceivedInvoiceManagementPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/ulazni-racuni/bez-narudzbe" element={<RequireAuth><AppShell><ExpenseWithoutOrderPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/nova" element={<RequireAuth><AppShell><ProcurementNewPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/gradiliste/:id" element={<RequireAuth><AppShell><ConstructionOverviewPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/odobrenja" element={<RequireAuth><AppShell><ProcurementApprovalsPage /></AppShell></RequireAuth>} />
          <Route path="/nabava/:id" element={<RequireAuth><AppShell><ProcurementCasePage /></AppShell></RequireAuth>} />
          <Route path="/postavke" element={<RequireAuth><AppShell><PostavkePage /></AppShell></RequireAuth>} />
          <Route path="/prijava" element={<LoginPage />} />
          <Route path="/skeneri" element={<RequireAuth><AppShell><ScannersPage /></AppShell></RequireAuth>} />
          <Route path="/ladica" element={<RequireAuth><AppShell desktopViewport><DocumentInboxReviewPage /></AppShell></RequireAuth>} />
          <Route path="/ladica/uvoz-paketa" element={<RequireAuth><AppShell><AgencyImportPage /></AppShell></RequireAuth>} />
          <Route
            path="/ladica/unos"
            element={<RequireAuth><AppShell desktopViewport><DocumentInboxUploadPage /></AppShell></RequireAuth>}
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppShell>
                  <PocetnaPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/sati"
            element={
              <RequireAuth>
                <AppShell>
                  <UnosSatiPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/zaposlenici"
            element={
              <RequireAuth>
                <AppShell>
                  <PopisZaposlenikaPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/rokovi-i-obveze"
            element={
              <RequireAuth>
                <AppShell>
                  <RokoviObvezePage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/obveze-tvrtke"
            element={
              <RequireAuth>
                <AppShell>
                  <ObvezeTvrtkePage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/gradilista"
            element={
              <RequireAuth>
                <AppShell>
                  <GradilistaPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route path="/gradilisni-spisak" element={<RequireAuth><AppShell><GradilisniPregledPage /></AppShell></RequireAuth>} />
          <Route
            path="/gradilisni-spisak/:gradilisteId"
            element={
              <RequireAuth>
                <AppShell>
                  <GradilisniSpisakPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/korisnici"
            element={
              <RequireAuth>
                <AppShell>
                  <KorisniciPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/zaposlenici/uvoz"
            element={
              <RequireAuth>
                <AppShell>
                  <UvozRadnikaPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/zaposlenici/novi"
            element={
              <RequireAuth>
                <AppShell>
                  <FormaRadnikaPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/zaposlenici/:radnikId/uredi"
            element={
              <RequireAuth>
                <AppShell>
                  <FormaRadnikaPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/zaposlenici/:radnikId"
            element={
              <RequireAuth>
                <AppShell>
                  <KarticaRadnikaPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Inside the shell like everything else. These two were the only
              screens without it, so a manager who opened them from the menu
              lost his navigation and had no way back except the browser's own
              button (Z-2). */}
          <Route
            path="/postavke/lozinka"
            element={
              <RequireAuth>
                <AppShell>
                  <ChangePasswordPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/postavke/dvofaktorska"
            element={
              <RequireAuth>
                <AppShell>
                  <TwoFactorPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes></PostavkeProvider>
      </RedProvider>
    </AuthProvider>
  );
}
